// THE CHARGE HALF OF A CLIENT-BORNE EXPENSE — the price, and what saving it will do.
//
// A PRICE IS A CHARGE. Typing what we charge the client and saving records the charge to
// collect in the same act (server/api/rest/expenses/charge.js): it lands on the money
// screen as money owed, dated today, and it stays there until it is collected (the money
// dot lights red only once it is late, like any invoice). There is no separate "record
// invoice" step for it any more. A blank price records nothing — the cost stays pending,
// "עוד לא נקבע", and the money dot is amber until a price is set.
//
// THE FIRST CHARGE ON A TOPIC CONVERTS IT, and that moves real money: its costs leave the
// retainer's profit and it gets a split of its own. So when this save would convert, the
// same notice the money screen shows appears here, with the real figures, and Save stays
// dark until it is acknowledged. The server refuses the conversion without it as well.
//
// Split out of ExpenseModal, which was already carrying the payment state and the fronter;
// the gate and its preview are a third concern, and they belong with the price.
import useAsync from '../hooks/useAsync';
import { collectionsApi } from '../api';
import he from '../i18n/he';
import { NumberInput } from './Form';
import ConversionNotice from './ConversionNotice';
import ExpenseChargeExisting from './ExpenseChargeExisting';

// What this save will do with the price. Read by the modal for its Save gate as well as
// by the fields below, so the two can never disagree about whether a conversion is coming.
//
//   charged        the cost already has its charge — the price and bearer are the
//                  invoice's business now, corrected through ./ExpenseChargeExisting
//   willCharge     this save raises a charge: a job cost the client bears with a price
//                  above zero and no live charge — a new row, one given its first price,
//                  or one whose charge was cancelled (expenses/service.js update)
export const useChargeState = ({ jobId, expense, form }) => {
  const isNew = !expense?.id;
  const charged = !!expense?.charged_on_invoice_id;
  const onClient = jobId != null && form.borne_by === 'client';
  const willCharge = onClient && !charged && Number(form.charge_amount) > 0;

  const { data: preview, loading, error } = useAsync(
    () => (willCharge
      ? collectionsApi.conversionPreviewForJob(jobId, isNew ? null : expense.id)
      : Promise.resolve(null)),
    [willCharge, jobId, expense && expense.id]
  );
  const converts = !!(willCharge && preview && preview.converts);
  return {
    charged, willCharge, preview: willCharge ? preview : null, converts,
    // Until the preview answers, nobody knows whether this save converts a topic — so it
    // cannot be acknowledged, and Save waits.
    previewPending: willCharge && loading,
    previewError: willCharge ? error : null,
  };
};

export default function ExpenseChargeFields({
  form, set, state, acknowledged, onAcknowledge, expense, onChargeAct,
}) {
  if (state.charged) return <ExpenseChargeExisting expense={expense} onAct={onChargeAct} />;
  return (
    <>
      <NumberInput label={he.expenses.chargeAmount} value={form.charge_amount} onChange={set('charge_amount')} />
      {state.willCharge && <p className="small-note"><strong>{he.expenses.chargeWillCreate}</strong></p>}
      {state.previewError && <p className="field-error">{he.expenses.chargePreviewFailed}</p>}
      <ConversionNotice
        preview={state.preview}
        amount={Number(form.charge_amount) || 0}
        cost={Number(form.amount) || 0}
        acknowledged={acknowledged}
        onAcknowledge={onAcknowledge}
      />
    </>
  );
}
